import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import OpenWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Commands/OpenWorkflowRun';
import openWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/openWorkflowRun';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import formatLongDate from '@UI/Helpers/formatLongDate';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import type FacilityWorkflowView from '@UI/Store/FacilityWorkflows/Views/FacilityWorkflowView';
import type WorkflowRunSummaryView from '@UI/Store/WorkflowRuns/Views/WorkflowRunSummaryView';
import { css } from '@emotion/react';
import { RefreshCw } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from '@verclam/synapse';
import { Button, Tag } from '@verclam/ui-kit';

const cardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.375rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};
`;

const headerStyle = css`
	display: flex;
	gap: 0.75rem;
	align-items: flex-start;
	justify-content: space-between;
	flex-wrap: wrap;
`;

const titleBlockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.3125rem;
	flex: 1;
	min-width: 14rem;
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	font-size: 1.3125rem;
	color: ${theme.colors.ink};
`;

const descriptionStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	line-height: 1.6;
	color: ${theme.colors.inkSecondary};
`;

const producedBoxStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const producedStepsStyle = css`
	display: flex;
	gap: 0.5rem 0.625rem;
	flex-wrap: wrap;
	align-items: baseline;
	margin: 0;
	padding: 0;
	list-style: none;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
	color: ${theme.colors.ink};
`;

const stepNumberStyle = css`
	font-family: ${theme.fonts.mono};
	color: ${theme.colors.accentDark};
`;

const arrowStyle = css`
	color: ${theme.colors.accent};
`;

const actionStyle = css`
	align-self: flex-start;
`;

const automaticLineStyle = css`
	display: flex;
	gap: 0.5rem;
	align-items: center;
	margin: 0;
	line-height: 1.5;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkSecondary};
`;

const validationNoteStyle = css`
	margin: 0;
	line-height: 1.5;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkSecondary};
`;

/** Lowercases a leading capital unless the word is an acronym ("PAP" stays "PAP"). */
const lowercaseFirst = (label: string): string =>
	label.length > 1 &&
	label.charAt(1) === label.charAt(1).toUpperCase() &&
	label.charAt(1) !== label.charAt(1).toLowerCase()
		? label
		: label.charAt(0).toLowerCase() + label.slice(1);

type Props = {
	readonly subject: SubjectAddress;
	readonly workflow: FacilityWorkflowView;
	/** The preparation of this group that prevents opening a new one, when there is one. */
	readonly existingPreparation: WorkflowRunSummaryView | null;
	readonly documentTypes: DocumentTypesListView | null;
	/** Whether the visitor may open or resume a preparation (permission, dossier not archived). */
	readonly canWrite: boolean;
};

/**
 * One group of documents proposed for the dossier: what it produces, stage by stage, what it carries
 * over automatically, and the single action — "Commencer" opens a preparation and goes to it,
 * "Reprendre" goes to the one already open. Leaf action component: it dispatches and navigates itself.
 */
const WorkflowCard: React.FC<Props> = ({
	subject,
	workflow,
	existingPreparation: existing,
	documentTypes,
	canWrite,
}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const [isOpening, setIsOpening] = React.useState(false);

	const labelOf = React.useCallback(
		(documentTypeId: number): string => documentTypes?.labelOf(documentTypeId) ?? 'Document',
		[documentTypes],
	);

	const stageLabels = React.useMemo(
		() =>
			workflow.stages
				.filter((stage) => stage.documentTypeIds.length > 0)
				.map((stage) => stage.documentTypeIds.map(labelOf).join(' + ')),
		[workflow.stages, labelOf],
	);

	const automaticLine = React.useMemo(() => {
		if (workflow.automaticSources.length === 0) {
			return null;
		}

		const sources = workflow.automaticSources.map(
			(source) =>
				`le dernier ${lowercaseFirst(labelOf(source.documentTypeId))}${source.verifiedOnly ? ' vérifié' : ''}`,
		);

		return `Reprend automatiquement ${sources.join(' et ')}.`;
	}, [workflow.automaticSources, labelOf]);

	const handleResumeClick = React.useCallback(() => {
		if (existing !== null) {
			navigate(`${buildSubjectPath(subject)}/preparations/${existing.id}`);
		}
	}, [navigate, subject, existing]);

	const handleStartClick = React.useCallback(() => {
		setIsOpening(true);
		dispatch(openWorkflowRun(new OpenWorkflowRun(facilityId, subject, workflow.key)))
			.then((action) => {
				if (action.type === 'WORKFLOW_RUN_OPENED') {
					navigate(`${buildSubjectPath(subject)}/preparations/${action.payload.id}`);
				}
			})
			.catch(ignoreHandledError)
			.finally(() => {
				setIsOpening(false);
			});
	}, [dispatch, navigate, facilityId, subject, workflow.key]);

	return (
		<div css={cardStyle}>
			<div css={headerStyle}>
				<div css={titleBlockStyle}>
					<h2 css={titleStyle}>{workflow.label}</h2>
					<ConditionalLoader data={workflow.description}>
						{(description) => <p css={descriptionStyle}>{description}</p>}
					</ConditionalLoader>
				</div>
				<ConditionalLoader data={existing}>
					{(blockingPreparation) => (
						<Tag
							rounded
							severity="warning"
							value={
								blockingPreparation.isOpen
									? `Préparation ouverte le ${formatLongDate(blockingPreparation.openedAt.toISOString())}`
									: 'Documents à valider'
							}
						/>
					)}
				</ConditionalLoader>
			</div>
			<div css={producedBoxStyle}>
				<Eyebrow>Produit</Eyebrow>
				<ol css={producedStepsStyle}>
					{stageLabels.map((stageLabel, index) => (
						<li key={stageLabel}>
							<ConditionalLoader data={index > 0}>
								<span css={arrowStyle} aria-hidden="true">
									→{' '}
								</span>
							</ConditionalLoader>
							<span css={stepNumberStyle}>{index + 1}.</span> {stageLabel}
						</li>
					))}
				</ol>
				<ConditionalLoader data={automaticLine}>
					{(line) => (
						<p css={automaticLineStyle}>
							<RefreshCw size={16} aria-hidden="true" />
							{line}
						</p>
					)}
				</ConditionalLoader>
			</div>
			<ConditionalLoader data={existing !== null && !existing.isOpen && existing}>
				{(toValidate) => (
					<p css={validationNoteStyle} role="note">
						Validez les documents de la préparation ouverte le{' '}
						{formatLongDate(toValidate.openedAt.toISOString())} avant d'en commencer une nouvelle.
					</p>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={canWrite}>
				{existing !== null ? (
					<Button
						css={actionStyle}
						type="button"
						severity="contrast"
						label="Reprendre"
						onClick={handleResumeClick}
					/>
				) : (
					<Button
						css={actionStyle}
						type="button"
						label="Commencer"
						loading={isOpening}
						onClick={handleStartClick}
					/>
				)}
			</ConditionalLoader>
		</div>
	);
};

export default WorkflowCard;
